import { t } from 'i18n';
import { CartSeller } from '@teko/ui-cart/desktop';
import { CartLine } from './CartLine';
import { CartItemGroup, CartItem } from '@teko/cart/v2';
import { Card, SvgIcon, styled } from '@teko/ui';
import React, { useState } from 'react';
import { useTenantConfig, useCartV2 } from 'contexts';
import { DetailComboModal } from 'components/desktop/organisms/Combo';
import { useCartCombo, useProductCombos } from 'hooks';
import {
  getComboPromotionId,
  getComboLineItems,
  checkEnableSellerCheckbox,
} from '../utils';
import CartComboList from './CartCombo/CartComboList';
import CartComboSuggestList from './CartCombo/CartComboSuggestList';
import { ProductSelectionModal } from 'components/desktop/organisms/Combo/ProductSelectionModal';
import {
  getDiscountByCombo,
  getSkusForProductsCombos,
  getTotalAmount,
  getTotalDiscount,
  mapComboWithKey,
} from 'helpers';
import { ProductComboScreen } from 'types';
import { getTrackingData } from '@teko/tracking';

interface CartLineListProps {
  onEditPrice?: (
    id: string,
    price: number,
    defaultExpectedPrice: number,
    maxConsultationServiceFee?: number,
  ) => void;
  itemMaximumQuantity?: number;
  isAllowEditPrice?: boolean;
  items: CartItemGroup[];
}

const VariantEditor = styled.div`
  color: #1990ff;
  line-height: 20px;
  font-size: 13px;
  display: flex;
  align-items: center;
  padding-left: 2.9rem;
  align-content: center;
  padding-bottom: 16px;
  cursor: pointer;
`;
const StyledSvgIcon = styled(SvgIcon)`
  margin-left: 9px;
`;

const StyledCard = styled(Card)`
  .card-body {
    padding-bottom: 0.25rem;
  }
`;

interface ComboModalProps {
  onClickCloseModal: () => void;
  onAddToCartCallback: () => void;
  visible: boolean;
  promotionId: number;
  comboLineItems: { sku: string; sellerSku?: string; quantity: number }[];
}

const ComboModal = ({
  onClickCloseModal,
  onAddToCartCallback,
  visible,
  promotionId,
  comboLineItems = [],
}: ComboModalProps) => {
  const comboSku = comboLineItems[0]?.sku || '';
  const { onAddComboToCart } = useCartCombo(comboSku);
  return (
    <DetailComboModal
      type="dynamic"
      onCloseModal={onClickCloseModal}
      visible={visible}
      onAddToCart={async (_, payload) => {
        await onAddComboToCart?.('dynamic', payload, true);
        onAddToCartCallback();
      }}
      promotionId={promotionId}
      comboLineItems={comboLineItems}
    />
  );
};

export const CartLineList = ({
  onEditPrice,
  items = [],
  isAllowEditPrice = false,
}: CartLineListProps) => {
  const [showComboModal, setShowComboModal] = useState(false);
  // keep track of selected combo for changing/adding product combo
  const [selectedCartItem, setSelectedCartItem] = useState<{
    item?: CartItem;
    showAllCombos: boolean;
  }>({ item: undefined, showAllCombos: false });

  const {
    onChangeQuantity,
    onRemoveItem,
    onSelectItem,
    onSelectSeller,
    onAddComboItems,
  } = useCartV2();
  const {
    uomCodeToDisplayDecimalQuantity,
    epicOmni1286,
    epicOmni1428,
    applyNewLogicToCalculateComboPrice = false,
  } = useTenantConfig();
  const {
    productCombosData,
    activeCombo,
    setActiveCombo,
    comboGroup,
    setComboGroup,
  } = useProductCombos({
    screen: ProductComboScreen.cart,
    skus: getSkusForProductsCombos(items),
    selectingSku: selectedCartItem.item?.sku,
  });

  if (items.length > 0) {
    const itemList = items.map((itemGroup, index) => {
      return (
        <StyledCard key={`${itemGroup.sellerId}-${index}`} marginBottom="1rem">
          <CartSeller
            name={itemGroup.title}
            checked={itemGroup.isSelected}
            onCheck={(checked: boolean) =>
              onSelectSeller(itemGroup.items, checked)
            }
            isAvatarHidden
            disabled={!checkEnableSellerCheckbox(itemGroup.items)}
          />
          {itemGroup.items.map((item: CartItem, itemIndex) => {
            // find the list combos that has the same sku with the main product of current combo return from cart
            const itemProductsCombo = productCombosData?.filter(
              (pd) => pd.sku === item.sku,
            );
            const currentCartComboInfo = {
              promotionIds: item.promotions?.length
                ? item.promotions[0].promotions.map((p) => p.promotionId)
                : [],
              skus: item.childItems?.length
                ? item.childItems.map((childItem) => childItem.sku)
                : [],
            };

            return (
              <React.Fragment key={item.id}>
                {epicOmni1286 &&
                  (!!item.childItems?.length ? (
                    <CartComboList
                      index={parseInt(`${index}${itemIndex}`)}
                      item={item}
                      onChangeComboProduct={(baseTotalProduct) => {
                        setSelectedCartItem({ item, showAllCombos: false });

                        // find the data of current active combo in combos list
                        const selectedProductCombo = mapComboWithKey(
                          itemProductsCombo,
                        )[0].filter(
                          (cb) =>
                            cb.totalProduct === baseTotalProduct &&
                            cb.conditions.length === item.childItems?.length &&
                            currentCartComboInfo.promotionIds.includes(
                              cb.promotion.id,
                            ),
                        );

                        const combo = {
                          ...selectedProductCombo[0],
                          conditions: selectedProductCombo?.[0]?.conditions.map(
                            ({ products, ...restConditions }) => ({
                              ...restConditions,
                              product: products.filter((p) =>
                                currentCartComboInfo.skus.includes(p.sku),
                              )[0],
                            }),
                          ),
                        };

                        setActiveCombo({
                          ...combo,
                          discountByCombo: getDiscountByCombo(
                            combo,
                            applyNewLogicToCalculateComboPrice,
                          ),
                          totalDiscount: getTotalDiscount(
                            combo,
                            applyNewLogicToCalculateComboPrice,
                          ),
                          totalAmount: getTotalAmount(
                            combo,
                            applyNewLogicToCalculateComboPrice,
                          ),
                        });
                        setShowComboModal(true);
                      }}
                      {...getTrackingData({
                        isTrackContent: true,
                        contentName: 'productComboList',
                        regionName: 'cartItem',
                        index: parseInt(`${index}${itemIndex}`),
                        ignoreinteraction: true,
                        payload: JSON.stringify({
                          isCombo: true,
                          comboPromotion:
                            currentCartComboInfo.promotionIds.length > 0
                              ? currentCartComboInfo.promotionIds[0]
                              : null,
                          sku: currentCartComboInfo.skus,
                        }),
                      })}
                    />
                  ) : itemProductsCombo.length > 0 ? (
                    <CartComboSuggestList
                      index={parseInt(`${index}${itemIndex}`)}
                      item={item}
                      productsCombo={itemProductsCombo[0]}
                      onAddComboProduct={() => {
                        setSelectedCartItem({ item, showAllCombos: true });

                        const productCombosWithKey =
                          mapComboWithKey(itemProductsCombo)[0];

                        const combo = {
                          ...productCombosWithKey[0],
                          conditions: productCombosWithKey?.[0]?.conditions.map(
                            ({ products, ...restConditions }) => ({
                              ...restConditions,
                              product: products?.[0],
                            }),
                          ),
                        };

                        // set active combo to 1st combo of combos list
                        setActiveCombo({
                          ...combo,
                          totalDiscount: getTotalDiscount(
                            combo,
                            applyNewLogicToCalculateComboPrice,
                          ),
                          totalAmount: getTotalAmount(
                            combo,
                            applyNewLogicToCalculateComboPrice,
                          ),
                          discountByCombo: getDiscountByCombo(
                            combo,
                            applyNewLogicToCalculateComboPrice,
                          ),
                        });

                        setShowComboModal(true);
                      }}
                      {...getTrackingData({
                        isTrackContent: true,
                        contentName: 'productComboList',
                        regionName: 'cartItem',
                        index: parseInt(`${index}${itemIndex}`),
                        ignoreinteraction: true,
                        payload: JSON.stringify({
                          isCombo: false,
                          comboPromotion:
                            currentCartComboInfo.promotionIds.length > 0
                              ? currentCartComboInfo.promotionIds[0]
                              : null,
                          sku: item.sku,
                        }),
                      })}
                    >
                      <CartLine
                        isViewOnly
                        showDeleteButton={false}
                        onChangeQuantity={async (
                          newQuantity: number,
                        ): Promise<void> => {
                          await onChangeQuantity(item, newQuantity, false);
                        }}
                        onRemove={async (): Promise<void> => {
                          await onRemoveItem(item);
                        }}
                        onEditPrice={() => {
                          const {
                            id,
                            price,
                            consultationFee,
                            onDemandDiscount,
                            maxConsultationFee,
                          } = item;
                          onEditPrice?.(
                            id,
                            price -
                              (consultationFee || 0) +
                              (onDemandDiscount || 0),
                            price,
                            maxConsultationFee || 0,
                          );
                        }}
                        item={item}
                        onCheck={() => onSelectItem(item)}
                        isAllowEditPrice={isAllowEditPrice}
                        isQuantityDecimal={
                          item.uomCode === uomCodeToDisplayDecimalQuantity
                        }
                        borderRadius="m"
                        showTierPromotions={!!epicOmni1428}
                        // show text not applicable with combo for tier promotions
                        // because product has suggest combos to buy more
                        isApplicableWithCombo={false}
                      />
                    </CartComboSuggestList>
                  ) : (
                    <CartLine
                      showCheck
                      onChangeQuantity={async (
                        newQuantity: number,
                      ): Promise<void> => {
                        await onChangeQuantity(item, newQuantity, false);
                      }}
                      onRemove={async (): Promise<void> => {
                        await onRemoveItem(item);
                      }}
                      onEditPrice={() => {
                        const {
                          id,
                          price,
                          consultationFee,
                          onDemandDiscount,
                          maxConsultationFee,
                        } = item;
                        onEditPrice?.(
                          id,
                          price -
                            (consultationFee || 0) +
                            (onDemandDiscount || 0),
                          price,
                          maxConsultationFee || 0,
                        );
                      }}
                      item={item}
                      onCheck={() => onSelectItem(item)}
                      isAllowEditPrice={isAllowEditPrice}
                      isQuantityDecimal={
                        item.uomCode === uomCodeToDisplayDecimalQuantity
                      }
                      showTierPromotions={!!epicOmni1428}
                    />
                  ))}
                {!epicOmni1286 && (
                  <>
                    <CartLine
                      showCheck
                      onChangeQuantity={async (
                        newQuantity: number,
                      ): Promise<void> => {
                        await onChangeQuantity(item, newQuantity, false);
                      }}
                      onRemove={async (): Promise<void> => {
                        await onRemoveItem(item);
                      }}
                      onEditPrice={() => {
                        const {
                          id,
                          price,
                          consultationFee,
                          onDemandDiscount,
                          maxConsultationFee,
                        } = item;
                        onEditPrice?.(
                          id,
                          price -
                            (consultationFee || 0) +
                            (onDemandDiscount || 0),
                          price,
                          maxConsultationFee || 0,
                        );
                      }}
                      item={item}
                      onCheck={() => onSelectItem(item)}
                      isAllowEditPrice={isAllowEditPrice}
                      isQuantityDecimal={
                        item.uomCode === uomCodeToDisplayDecimalQuantity
                      }
                      showTierPromotions={!!epicOmni1428}
                    />
                    {!!item.childItems?.length ? (
                      <>
                        <VariantEditor onClick={() => setShowComboModal(true)}>
                          <span>{t('Tùy chọn sản phẩm trong combo')}</span>
                          <StyledSvgIcon
                            name={'chevron-forward'}
                            color={'#1990FF'}
                            size={16}
                          />
                        </VariantEditor>
                        <ComboModal
                          visible={showComboModal}
                          comboLineItems={getComboLineItems(
                            item.childItems,
                            item.quantity,
                          )}
                          onAddToCartCallback={() => setShowComboModal(false)}
                          onClickCloseModal={() => setShowComboModal(false)}
                          promotionId={getComboPromotionId(
                            item.promotions || [],
                          )}
                        />
                      </>
                    ) : null}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </StyledCard>
      );
    });
    return (
      <>
        {itemList}
        {epicOmni1286 && showComboModal && selectedCartItem.item && (
          <ProductSelectionModal
            showAddComboToCartButton={false}
            showAllCombos={selectedCartItem.showAllCombos}
            closeModal={() => {
              setShowComboModal(false);
              setSelectedCartItem({ item: undefined, showAllCombos: false });
              setActiveCombo(undefined);
            }}
            visible={showComboModal}
            handleConfirm={async (selectingCombo) => {
              if (selectingCombo) {
                const mainPayload = {
                  override: true,
                  overrideItemId: selectedCartItem.item?.id,
                  groups: [
                    {
                      quantity: 1,
                      promotionId: selectingCombo.promotion.id,
                      products: selectingCombo.conditions.map((el, index) => ({
                        sku: el.product.sku,
                        promotionId: !!el.product.bestPromotionIds?.length
                          ? el.product.bestPromotionIds?.[0]
                          : null,
                        quantity: el.requiredQuantity,
                        isPrimary: index === 0,
                      })),
                    },
                  ],
                };
                await onAddComboItems(mainPayload, false);
              }
            }}
            productCombos={
              selectedCartItem.item
                ? mapComboWithKey(
                    productCombosData?.filter(
                      (pd) => pd.sku === selectedCartItem.item?.sku,
                    ),
                  )[0]
                : undefined
            }
            {...{
              activeCombo,
              setActiveCombo,
              comboGroup,
              setComboGroup,
            }}
          />
        )}
      </>
    );
  }
  return null;
};
